import { computed, ComputedRef, watch } from 'vue';
import { defineStore } from 'pinia';

import i18n from '@/i18n';
import MasterService from '@/services/imports/manifest/master.service';

import { MBL_DEFAULT_FILTERS, MBL_FORM_DEFAULT_DATA } from '@/constants/master-bl';

import { MblEntity } from '@/types/mbl/mbl.entity';
import { MblFilters } from '@/types/mbl/mbl.filters';
import { MilestoneEntity } from '@/types/common/milestone.entity';
import { useFilters } from '@/composables/filters/useFilters';
import { useState } from '@/composables/common/useState';
import { useToggle } from '@/composables/useToggle';
import { useToast } from '@/composables/useToast';
import { AxiosError } from 'axios';
import { useManifestHasInvoicedCharges } from '@/composables/charges/manifest/useManifestHasInvoicedCharges';
import { useQueryClient } from '@tanstack/vue-query';
import { useOrganizationStore } from '../useOrganizationStore';
import { useOnlineStore } from '../useOnlineStore';

export const useMBLStore = defineStore('MBL', () => {
  const [mbl, setMbl] = useState<MblEntity | null>(null);
  const [allMbl, setAllMbl] = useState<MblEntity[]>([]);
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [milestones, setMilestones] = useState<MilestoneEntity[]>([]);
  const [readonly, toggleReadonly] = useToggle(true);
  const [mblFormData, setMblFormData] = useState(MBL_FORM_DEFAULT_DATA());

  const queryClient = useQueryClient();

  const { hasInvoicedCharges, checkManifestHasInvoicedCharges } = useManifestHasInvoicedCharges();

  const toast = useToast();

  const organizationStore = useOrganizationStore();

  const mblData: ComputedRef<MblEntity | null> = computed(() => mbl.value);

  const filtersModule = useFilters<MblEntity, Partial<MblFilters>>(MasterService, {
    initialFilters: MBL_DEFAULT_FILTERS(),
    watcher: true,
  });

  const create = async (payload: any) => {
    await validateContainer(payload.mblNumber, payload.containerNumber);
    const { data: result } = await MasterService.create(payload);
    if (!result) {
      return { success: false, message: result.message || i18n.t('ServerErrorTryAgainLater') };
    }
    return { success: true, message: i18n.t('MBLCreatedSuccessfully'), data: result };
  };
  const update = async ({ id, payload }: any) => {
    if (!id) {
      throw new Error('MBL data is incorrect.');
    }
    await validateContainer(payload.mblNumber, payload.containerNumber, id);
    const { data: result } = await MasterService.update({ ...payload, _id: id });
    if (!result) {
      return { success: false, message: result.message || i18n.t('ServerErrorTryAgainLater') };
    }

    await fetchMbl(id);
    await fetchMilestones(id);
    toggleReadonly(true);

    return { success: true, message: i18n.t('MBLSavedSuccessfully') };
  };

  const updateCloseStatus = async (mblId: string) => {
    try {
      const { data: mbl } = await MasterService.close(mblId);
      await filtersModule.fetchFilteredItems();
      setMbl(mbl);
    } catch {
      toast.error('Error while closing hbl');
    }
  };

  const deleteManifest = async (id: string) => {
    try {
      await MasterService.delete(id);
      await filtersModule.fetchFilteredItems();
    } catch (error) {
      toast.error((error as AxiosError).response?.data as string);
      throw error;
    }
  };
  const fetchAllMbl = async () => {
    try {
      const { data: items } = await MasterService.getAll();
      setAllMbl(items);
    } catch (e) {
      console.log('fetchAllMbl', e);
      throw e;
    }
  };

  const fetchMbl = async (id: any) => {
    try {
      const { data } = await MasterService.get(id);
      setMbl(data);
    } catch (e) {
      console.log('fetchMbl', e);
      throw e;
    }
  };
  const fetchMilestones = async (id: any) => {
    try {
      const { data } = await MasterService.getMilestones(id);
      setMilestones(data);
    } catch (e) {
      console.log('fetchMblMilestones', e);
    }
  };

  const validateContainer = async (mblNumber: string, containerNumber: string, mblId?: string) => {
    const { data } = await MasterService.validateContainerStatus(mblNumber, containerNumber, mblId);
    if (!data.status) {
      toast.error('Duplication of MBL # / Container # ! Please change the values!');
      throw new Error('MBL#/Container# duplication');
    }
    return data.status;
  };

  const updateViewStatus = async (id: string, mbl: MblEntity) => {
    const payload = {
      ...mbl,
      isViewed: true,
    };
    await update({
      id,
      payload,
    });
    await queryClient.invalidateQueries(['receive-count']);
  };

  const finalize = async (mblId: string) => {
    const { data } = await MasterService.validateForFinalize(mblId);
    if (!data.status) {
      let message = 'MBL is not ready for finalizing! <br />';
      if (!data.mblNumber) message = message.concat('- MBL # should be entered <br />');
      if (!data.containerNumber) message = message.concat('- Container # should be entered <br />');
      if (!data.customer) message = message.concat('- Customer should be selected <br />');
      if (!data.stripDate) message = message.concat('- Strip Date should be entered <br />');
      if (!data.closedate) message = message.concat('- File should not be closed <br />');
      if (!data.hbls) message = message.concat('- At least one valid HBL should be present <br />');
      return toast.info(message);
    }
    try {
      const { data: finalizationResponse } = await MasterService.finalize(mblId);
      return finalizationResponse;
    } catch (e) {
      toast.info(e?.response?.data || 'Error while finalizing!');
    }
  };

  function formatWarehouseCode(code: string) {
    if (!code) return '';
    return code.length > 2 ? code.slice(0, 2) + ' ' + code.slice(2) : code;
  }

  const fetchVesselDetails = async () => {
    try {
      const warehouseCode = organizationStore.currentWarehouse?.code ?? '';
      const { data } = await MasterService.getVesselDetails(formatWarehouseCode(warehouseCode));
      return data;
    } catch (e) {
      console.log('fetchVesselDetails', e);
      throw e;
    }
  };

  const onlineStore = useOnlineStore();

  watch(readonly, (value) => {
    value ? onlineStore.stopUserPageEdit() : onlineStore.startUserPageEdit();
  });

  return {
    mbl,
    allMbl,
    milestones,
    hasChanges,
    mblFormData,
    hasInvoicedCharges,

    setHasChanges,
    setMblFormData,

    readonly,
    toggleReadonly,

    mblData,

    finalize,
    create,
    update,
    updateCloseStatus,
    updateViewStatus,
    fetchMbl,
    fetchAllMbl,
    deleteManifest,
    fetchMilestones,
    checkManifestHasInvoicedCharges,
    fetchVesselDetails,
    ...filtersModule,
  };
});
